// src/firebase/examMaterials.js
// Storage helpers for exam-mode content the head marker authors: per-section
// Exhibit/Requirement documents, and the one global Tax Tables/Help document.
// Firestore metadata for exhibits/requirements lives on sectionStructures
// (see settings.js/updateSectionStructures) - this module only handles the
// Storage side (upload/delete), mirroring the existing createSubmission
// pattern of storing a Storage path, not a tokenized URL.

import { ref, uploadBytesResumable, deleteObject } from 'firebase/storage';
import { storage } from './config';

// Students shouldn't see ".pdf" tacked onto every document name - strip the
// extension from the uploaded filename when defaulting a document's title.
function titleFromFileName(fileName) {
  return fileName.replace(/\.[^./\\]+$/, '');
}

/**
 * Upload an Exhibit or Requirement document for a specific homework/section.
 * kind: 'exhibits' | 'requirements'
 * Returns the metadata object to store on that section's exhibits/requirements array.
 */
export const uploadExamMaterial = async (homeworkType, sectionId, kind, file) => {
  const uniqueSuffix = Date.now();
  const filePath = `examMaterials/${homeworkType}/${sectionId}/${kind}/${uniqueSuffix}_${file.name}`;
  const fileRef = ref(storage, filePath);
  await uploadBytesResumable(fileRef, file);
  return { id: String(uniqueSuffix), title: titleFromFileName(file.name), filePath, fileName: file.name };
};

/**
 * Delete an exam material file from Storage (Firestore array update is the
 * caller's responsibility, same division of concerns as elsewhere)
 */
export const deleteExamMaterialFile = async (filePath) => {
  try {
    await deleteObject(ref(storage, filePath));
  } catch (error) {
    console.error('Delete exam material file error:', error);
  }
};

/**
 * Upload the single global Tax Tables / Help document, reused by every
 * exam-mode homework
 */
export const uploadTaxTablesDocument = async (file) => {
  const filePath = `examMaterials/_global/taxTables/${Date.now()}_${file.name}`;
  const fileRef = ref(storage, filePath);
  await uploadBytesResumable(fileRef, file);
  return { filePath, fileName: file.name, title: titleFromFileName(file.name) };
};
